/* eslint-disable */
// Util file import, disable eslint for now

import type { SxProps, Theme } from '@mui/material/styles';

type HexColor = string;
type RgbaColor = string;
type RgbColor = [number, number, number];

type ColorString = HexColor | RgbaColor;
type ColorShades = {
  50: ColorString;
  100: ColorString;
  200: ColorString;
  300: ColorString;
  400: ColorString;
  500: ColorString;
  600: ColorString;
  700: ColorString;
  800: ColorString;
  900: ColorString;
};

/**
 * Converts a hex color string to an RGB array.
 * @param hex - The hex color string (with or without leading #)
 * @returns An array of [r, g, b] values (0-255)
 */
const hexToRgb = (hex: HexColor): RgbColor => {
  const shorthandHexRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const fullHexRegex = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;
  const fallbackColor: RgbColor = [0, 0, 0];
  // if shorthand notation is used, expand it to full form
  hex = hex.replace(shorthandHexRegex, (_, r, g, b) => r + r + g + g + b + b);
  // match the full hex notation
  const result = fullHexRegex.exec(hex);
  // return the RGB values or a fallback color
  return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : fallbackColor;
};

const createTransparentColor = (hexColor: HexColor, opacity: number): RgbaColor => {
  const [r, g, b] = hexToRgb(hexColor);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const generateTailwindColorMap = (palette: Record<string, any>, prefix: string = ''): Record<string, string> => {
  return Object.keys(palette).reduce((acc, key) => {
    const value = palette[key];
    const newPrefix = prefix ? `${prefix}-${key}` : key;

    if (typeof value === 'object' && !Array.isArray(value)) {
      return { ...acc, ...generateTailwindColorMap(value, newPrefix) };
    }

    return { ...acc, [newPrefix]: value };
  }, {});
};

export type ChipLevels =
  | 'Default'
  | 'Secondary'
  | 'Success'
  | 'Information'
  | 'Warning'
  | 'Critical'
  | 'AI'
  | 'Neutral'
  | 'notification';

const getChipSxStylingForLevel = (level: ChipLevels, baseObject: Record<string, unknown> = {}): SxProps<Theme> => {
  const obj = baseObject;
  switch (level) {
    case 'Default':
      obj['border'] = '1px solid #1A1A1A';
      obj['backgroundColor'] = '#1A1A1A';
      break;
    case 'Secondary':
      obj['border'] = '1px solid #D1D1D1';
      obj['backgroundColor'] = '#FDFDFD';
      break;
    case 'Success':
      obj['border'] = '1px solid #A7DDAC';
      obj['backgroundColor'] = '#ECF8ED';
      break;
    case 'Information':
      obj['border'] = '1px solid #8BC3E5';
      obj['backgroundColor'] = '#E6F2F9';
      break;
    case 'Warning':
      obj['border'] = '1px solid #FFDDBF';
      obj['backgroundColor'] = '#FFF8F1';
      break;
    case 'Critical':
      obj['border'] = '1px solid #F7BAB6';
      obj['backgroundColor'] = '#FDF0EF';
      break;
    case 'AI':
      obj['border'] = '1px solid #C0B2FF';
      obj['backgroundColor'] = '#F4F2FF';
      break;
    case 'notification':
      obj['border'] = '1px solid #E74C3C';
      obj['backgroundColor'] = '#E74C3C';
      break;
    case 'Neutral':
      obj['border'] = '1px solid #d1d1d1';
      obj['backgroundColor'] = '#ffffff';
      break;
  }
  return obj as SxProps<Theme>;
};

export {
  type ColorString,
  type ColorShades,
  hexToRgb,
  createTransparentColor,
  generateTailwindColorMap,
  getChipSxStylingForLevel,
};
